'use client';

import styles from '../blogComponent.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Loader } from '@/components';
import { fetchPosts } from '@/utils/fetchPosts';
import { BlogProps } from '@/types/blog.types';

type BlogMaineProps = {
  currentPage?: number;
};

const BlogMaine = ({ currentPage = 1 }: BlogMaineProps) => {
  const [blogs, setBlogData] = useState<BlogProps[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const router = useRouter();
  const blogsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      const posts = await fetchPosts();
      setBlogData(posts);
      const totalPagesCount = Math.ceil(posts.length / blogsPerPage);
      setTotalPages(totalPagesCount);

      // Redirect if current page exceeds total pages
      if (currentPage > totalPagesCount && totalPagesCount > 0) {
        if (totalPagesCount === 1) {
          router.push('/blog');
        } else {
          router.push(`/blog/page/${totalPagesCount}`);
        }
      }
    };
    fetchData();
  }, [currentPage, router]);

  const getCurrentPageBlogs = () => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    const endIndex = startIndex + blogsPerPage;
    return blogs.slice(startIndex, endIndex);
  };

  const currentPageBlogs = getCurrentPageBlogs();

  return (
    <div className={styles.blogComponentContent}>
      {blogs.length === 0 ? (
        <Loader />
      ) : (
        <>
          <div className={styles.blogComponentMain}>
            <div className={styles.blogComponentTop}>
              {currentPageBlogs.length > 0 && (
                <Link
                  href={
                    currentPageBlogs[0].link.startsWith('/')
                      ? currentPageBlogs[0].link
                      : `/${currentPageBlogs[0].link}`
                  }
                  className={styles.blogComponentLeftItem}
                >
                  <Image
                    src={currentPageBlogs[0].picture}
                    alt={currentPageBlogs[0].title}
                    width={585}
                    height={330}
                    unoptimized
                    className={styles.blogComponentLeftItemImage}
                  />
                  <div
                    className={styles.blogComponentLeftItemTitle}
                    dangerouslySetInnerHTML={{
                      __html: currentPageBlogs[0].title,
                    }}
                  ></div>
                  <div className={styles.blogComponentLeftItemDescription}>
                    {currentPageBlogs[0].description}
                  </div>
                </Link>
              )}
              <div className={styles.blogComponentRightItem}>
                {currentPageBlogs.slice(1, 4).map((item, index) => (
                  <Link
                    href={
                      item.link.startsWith('/') ? item.link : `/${item.link}`
                    }
                    key={index}
                  >
                    <div className={styles.blogComponentRightItemWrapper}>
                      <Image
                        src={item.picture}
                        unoptimized
                        alt={item.title}
                        width={166}
                        height={126}
                        className={styles.blogComponentRightItemImage}
                      />
                      <div className={styles.blogComponentRightItemContent}>
                        <div
                          className={styles.blogComponentRightItemTitle}
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        ></div>
                        <div
                          className={styles.blogComponentRightItemDescription}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        ></div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className={styles.blogComponentBottom}>
              {currentPageBlogs.slice(4).map((item, index) => (
                <Link
                  href={item.link.startsWith('/') ? item.link : `/${item.link}`}
                  key={index}
                  className={`${index === 0 ? styles.blogComponentLeftItemBottom : styles.blogComponentRightItemBottom}`}
                >
                  <div className={styles.blogComponentRightItemWrapper}>
                    <Image
                      src={item.picture}
                      alt={item.title}
                      unoptimized
                      width={166}
                      height={126}
                      className={styles.blogComponentRightItemImage}
                    />
                    <div>
                      <div
                        className={styles.blogComponentRightItemTitle}
                        dangerouslySetInnerHTML={{ __html: item.title }}
                      ></div>
                      <div
                        className={styles.blogComponentRightItemDescription}
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      ></div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              {currentPage > 1 ? (
                <Link
                  href={
                    currentPage === 2
                      ? '/blog'
                      : `/blog/page/${currentPage - 1}`
                  }
                  className={styles.paginationArrow}
                >
                  &#8249;
                </Link>
              ) : (
                <span
                  className={`${styles.paginationArrow} ${styles.paginationArrowDisabled || ''}`}
                >
                  &#8249;
                </span>
              )}

              {(() => {
                const maxVisiblePages = 6;
                const pages = [];

                if (totalPages <= maxVisiblePages) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  if (currentPage < 3) {
                    for (let i = 1; i <= 3; i++) {
                      pages.push(i);
                    }
                    pages.push('...');
                    pages.push(totalPages);
                  } else if (currentPage >= totalPages - 2) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = totalPages - 3; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    pages.push('...');
                    pages.push(currentPage - 1);
                    pages.push(currentPage);
                    pages.push(currentPage + 1);
                    pages.push('...');
                    pages.push(totalPages);
                  }
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`dots-${index}`}
                        className={styles.paginationDots}
                      >
                        ...
                      </span>
                    );
                  }

                  const href = page === 1 ? '/blog' : `/blog/page/${page}`;

                  return (
                    <Link
                      key={page}
                      href={href}
                      className={`${styles.paginationButton} ${
                        page === currentPage
                          ? styles.paginationButtonActive
                          : ''
                      }`}
                    >
                      {page}
                    </Link>
                  );
                });
              })()}

              {currentPage < totalPages ? (
                <Link
                  href={`/blog/page/${currentPage + 1}`}
                  className={styles.paginationArrow}
                >
                  &#8250;
                </Link>
              ) : (
                <span
                  className={`${styles.paginationArrow} ${styles.paginationArrowDisabled || ''}`}
                >
                  &#8250;
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlogMaine;
